import cx from 'classnames'
import { useMemo, useRef } from 'react'

import useFocusWithinDetection from '@js/common/hooks/useFocusWithinDetection'

import CTA from '@f/elements/CTA/CTA'

import DesktopNavigationMenuItem from '@f-fertighaus/layout/PageHeader/partials/DesktopMenuItem'

import { MenuItemType, SubMenuItem, usePageHeader } from '../PageHeaderSSR'
import { handleMenuItemClickType } from '../PageHeaderStandard'

interface DesktopNavProps {
  onClick: handleMenuItemClickType
  currentActiveMenu: MenuItemType | null
  closeAllMenus: () => void
  postcode: string
  keepActiveMenuOpen: boolean
}

const splitMenuItemsIntoColumns = (menuItems?: SubMenuItem[]) => {
  if (!menuItems) {
    return []
  }
  const columns = menuItems.reduce(
    (acc: [SubMenuItem[]], currentItem) => {
      if (currentItem.force_new_column && acc[acc.length - 1].length > 0) {
        acc.push([currentItem])
      } else {
        acc[acc.length - 1].push(currentItem)
      }
      return acc
    },
    [[]]
  )

  return columns
}

export const DesktopNav = ({
  closeAllMenus,
  onClick,
  currentActiveMenu,
  keepActiveMenuOpen,
  postcode
}: DesktopNavProps) => {
  const { menu_items: menuItems } = usePageHeader()
  const containerRef = useRef(null)

  const columns = useMemo(() => {
    return splitMenuItemsIntoColumns(
      menuItems?.filter(({ type }) => type === currentActiveMenu)[0]
        ?.sub_menu_items
    )
  }, [currentActiveMenu, menuItems])

  useFocusWithinDetection({
    containerRef,
    onFocusOut: closeAllMenus
  })

  return (
    <nav className='[ f-m-l-040 ] PageHeader-navigation-row' ref={containerRef}>
      <ul className='[ f-!list ] PageHeader-nav'>
        {menuItems
          ?.filter(menuItem => !menuItem.only_mobile)
          .map(menuItem => (
            <DesktopNavigationMenuItem
              key={`desktop_menu_${menuItem.type}`}
              menuItem={menuItem}
              isOpen={currentActiveMenu === menuItem.type}
              keepOpen={keepActiveMenuOpen}
              postcode={postcode}
              onCTAMouseEnter={
                menuItem.sub_menu_items
                  ? (e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault()
                      if (currentActiveMenu === menuItem.type) {
                        return
                      }
                      onClick(menuItem.type, false)
                    }
                  : closeAllMenus
              }
              onCTAClick={
                menuItem.sub_menu_items
                  ? (e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault()
                      onClick(menuItem.type, true)
                    }
                  : closeAllMenus
              }
            />
          ))}
      </ul>
      <div
        className={cx(
          'PageHeader-menu-fullwidth',
          currentActiveMenu &&
            menuItems &&
            menuItems.filter(
              menuItem =>
                menuItem.type === currentActiveMenu &&
                menuItem.sub_menu_items.length > 0 &&
                !menuItem.className
            ).length > 0
            ? ''
            : 'f-visually-hidden'
        )}>
        <div className='f-c-ba-whi f-p-v-030 f-p-h-020 PageHeader-menu-fullwidth-content'>
          <div className='[ f-wrap ]'>
            <div className='[ f-g-r ]'>
              {columns.map((column, index) => (
                <div key={index} className='f-d-flc f-fl-gap-020 f-g-c6'>
                  {column.map(subMenuItem => (
                    <div
                      key={`sub_menu_item_${
                        subMenuItem.label || subMenuItem.title
                      }`}
                      className={cx(
                        'f-d-flc f-fl-gap-020',
                        subMenuItem.className
                      )}>
                      {subMenuItem.url ? (
                        <CTA
                          tag='a'
                          type='text-primary'
                          className='[ f-t-tbody f-pos-rel f-t-ellip ] PageHeader-sub-menu-header is-block'
                          href={subMenuItem.url}
                          onClick={closeAllMenus}
                          wtData={{
                            'CTA-click': {
                              location: 'header'
                            }
                          }}>
                          {subMenuItem.label}
                        </CTA>
                      ) : (
                        <div className='f-c-fo-pri f-t-tbody PageHeader-sub-menu-header'>
                          {subMenuItem.label}
                        </div>
                      )}
                      {subMenuItem.sub_menu_links?.map(subMenuLink => (
                        <CTA
                          key={`sub_menu_link_${subMenuLink.label}`}
                          tag='a'
                          type='text-primary'
                          className='[ f-t-mini f-t-ellip ] is-block'
                          href={subMenuLink.url}
                          onClick={closeAllMenus}
                          wtData={{
                            'CTA-click': {
                              location: 'header'
                            }
                          }}>
                          {subMenuLink.label.trim()}
                        </CTA>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}
